import type { Doc } from "building-types/doc";
import type { DocEntityId } from "building-types/doc/docEntityId";
import type { ContentTemplateHandle, PlanSetTemplateHandle } from "building-types/handles/docEntityHandleTypes";
import type { AnnotationDrawingTemplate, ContentTemplate, TagTemplate } from "building-types/views/planSets";
import { type ContentTemplateInstance, type DrawingTemplate, type SheetTemplate } from "building-types/views/planSets";
import type { Table, RWTable } from "common/data/table";
import objectFilter from "common/utils/objectFilter";
import {
  isAnnotationDrawingTemplate,
  isDrawingTemplate,
  isGenericDrawingTemplate,
  isViewportTitleDrawingTemplate,
  TAG_TEMPLATE_ENTITY_PREDICATES,
} from "./contentTemplateTools";
import { SystemDrawingTemplates } from "./systemDrawingTemplates";

export function docGetContentTemplateInstances(
  doc: Pick<Doc, "contentTemplateInstances">,
  handle: ContentTemplateHandle | PlanSetTemplateHandle,
): Table<ContentTemplateInstance> {
  const contentTemplateInstances: RWTable<ContentTemplateInstance> = {};

  for (const id in doc.contentTemplateInstances) {
    const contentTemplateInstance = doc.contentTemplateInstances[id];

    if (contentTemplateInstance.contentTemplateId === handle.id) {
      contentTemplateInstances[id] = contentTemplateInstance;
    }
  }

  return contentTemplateInstances;
}

export function docGetDrawingTemplates(doc: Pick<Doc, "contentTemplates">) {
  return objectFilter(doc.contentTemplates, isDrawingTemplate) as Table<DrawingTemplate>;
}

export function docGetSheetTemplates(doc: Doc) {
  return objectFilter(
    doc.contentTemplates,
    (contentTemplate) => contentTemplate.templateType === "SHEET",
  ) as Table<SheetTemplate>;
}

function getSystemTemplatesOfType<T extends DrawingTemplate["drawingType"]>(
  type: T,
): RWTable<Extract<DrawingTemplate, { drawingType: T }>> {
  const drawingTemplates = {};
  for (const id in SystemDrawingTemplates) {
    const template = SystemDrawingTemplates[id];
    if (template.drawingType === type) {
      drawingTemplates[id] = template;
    }
  }

  return drawingTemplates;
}

export function docGetViewportTitleDrawingTemplates(doc: Pick<Doc, "contentTemplates">) {
  const viewportTitleDrawingTemplates = getSystemTemplatesOfType("VIEWPORT_TITLE");

  for (const id in doc.contentTemplates) {
    const contentTemplate = doc.contentTemplates[id];
    if (isViewportTitleDrawingTemplate(contentTemplate)) {
      viewportTitleDrawingTemplates[id] = contentTemplate;
    }
  }

  return viewportTitleDrawingTemplates;
}

export function docGetGenericDrawingTemplates(doc: Pick<Doc, "contentTemplates">) {
  return objectFilter(doc.contentTemplates, isGenericDrawingTemplate) as Table<DrawingTemplate>;
}

export function docGetAnnotationDrawingTemplates(doc: Pick<Doc, "contentTemplates">) {
  return objectFilter(doc.contentTemplates, isAnnotationDrawingTemplate) as Table<AnnotationDrawingTemplate>;
}

export function docGetTagTemplates(
  doc: Pick<Doc, "contentTemplates">,
  targetEntityType: TagTemplate["targetEntityType"],
) {
  const tagTemplates = getSystemTemplatesOfType("TAG");
  const tagTemplateFilter = TAG_TEMPLATE_ENTITY_PREDICATES[targetEntityType];

  for (const id in doc.contentTemplates) {
    const contentTemplate = doc.contentTemplates[id];
    if (tagTemplateFilter(contentTemplate)) {
      tagTemplates[id] = contentTemplate;
    }
  }

  return tagTemplates;
}

export function docGetContentTemplate(
  doc: Pick<Doc, "contentTemplates">,
  handle: ContentTemplateHandle,
): ContentTemplate | undefined {
  return doc.contentTemplates[handle.id] ?? SystemDrawingTemplates[handle.id];
}

export function isTagDrawingTemplateId(
  doc: Pick<Doc, "contentTemplates">,
  tagTemplateId?: DocEntityId<"CONTENT_TEMPLATE">,
): tagTemplateId is DocEntityId<"CONTENT_TEMPLATE"> {
  if (!tagTemplateId) {
    return false;
  }

  const contentTemplate = docGetContentTemplate(doc, { type: "CONTENT_TEMPLATE", id: tagTemplateId });
  return !!contentTemplate && isDrawingTemplate(contentTemplate) && contentTemplate.drawingType === "TAG";
}
